import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api } from "@/lib/api.js";
import { useAuthStore } from "@/lib/auth.js";

const MIN = 10;

function validate(pwd: string, current: string): string | null {
  if (pwd.length < MIN) return `Debe tener al menos ${MIN} caracteres`;
  if (!/[A-Z]/.test(pwd)) return "Debe incluir al menos una mayúscula";
  if (!/[a-z]/.test(pwd)) return "Debe incluir al menos una minúscula";
  if (!/[0-9]/.test(pwd)) return "Debe incluir al menos un número";
  if (!/[^A-Za-z0-9]/.test(pwd)) return "Debe incluir al menos un símbolo";
  if (pwd === current) return "La nueva contraseña debe ser distinta a la actual";
  return null;
}

export function ChangePasswordPage() {
  const nav = useNavigate();
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  const mustChange = useAuthStore((s) => s.mustChangePassword);
  const clearMustChange = useAuthStore((s) => s.clearMustChange);
  const user = useAuthStore((s) => s.user);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthed) return <Navigate to="/login" replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("La confirmación no coincide");
      return;
    }
    const v = validate(next, current);
    if (v) {
      setError(v);
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword: current,
        newPassword: next,
      });
      clearMustChange();
      toast.success("Contraseña actualizada");
      nav("/", { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo actualizar";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-full grid place-items-center bg-slate-50 dark:bg-slate-950 p-6">
      <form onSubmit={onSubmit} className="card w-[440px] p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-500 grid place-items-center text-white">
            <Lock size={18} />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Cambio de contraseña obligatorio</h1>
            <p className="text-xs text-slate-500">
              {mustChange
                ? "Por seguridad, debes cambiar tu contraseña antes de continuar."
                : "Actualiza tu contraseña cuando gustes."}
            </p>
          </div>
        </div>

        <div className="rounded-md bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 text-sm p-3">
          <p className="text-slate-500">Cuenta</p>
          <p className="font-mono text-xs">{user?.email}</p>
        </div>

        {error && (
          <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm p-3">
            {error}
          </div>
        )}

        <div className="space-y-1">
          <label className="text-sm font-medium">Contraseña actual</label>
          <input
            type="password"
            className="input"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Nueva contraseña</label>
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
          <p className="text-xs text-slate-500">
            Mínimo 10 caracteres con mayúscula, minúscula, número y símbolo.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Confirmar nueva contraseña</label>
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="btn-primary w-full" disabled={loading}>
          {loading ? "Guardando…" : "Actualizar contraseña"}
        </button>
      </form>
    </div>
  );
}
